import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, User, Loader, LogOut, MessageCircle, X, Menu, Home, Calendar, MapPin, Book, Briefcase, GraduationCap, Church,  } from 'lucide-react';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { getFirestore, collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { initializeApp } from 'firebase/app';
import Header from './Header';
import Footer from './Footer';

// Initialize Firebase with your credentials
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase services
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

export default function Users() {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);

  // Check if user is authenticated
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
      } else {
        window.location.href = '/signup';
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
    setSearchPerformed(false);
  };

  const performSearch = async () => {
    if (!searchTerm.trim()) return;
    
    setIsSearching(true);
    setSearchResults([]);
    setSearchPerformed(false);
    
    try {
      console.log("Search term:", searchTerm);
      const results = [];
      
      const usersRef = collection(db, "users");
      const usersSnapshot = await getDocs(usersRef);
      
      for (const userDoc of usersSnapshot.docs) {
        if (userDoc.id !== currentUser?.uid) {
          const userData = userDoc.data();
          
          if (userData.username && userData.username.toLowerCase() === searchTerm.trim().toLowerCase()) {
            const profileRef = doc(db, "profiles", userDoc.id);
            const profileSnapshot = await getDoc(profileRef);
            const profileData = profileSnapshot.exists() ? profileSnapshot.data() : {};
            
            results.push({
              id: userDoc.id,
              username: userData.username || "",
              photoUrl: userData.profilePic || profileData.photoUrl || "",
              firstName: profileData.firstName || "",
              lastName: profileData.lastName || "",
              age: profileData.age || "",
              gender: profileData.gender || "",
              ethnicity: profileData.ethnicity || "",
              height: profileData.height || "",
              hairColor: profileData.hairColor || "",
              eyeColor: profileData.eyeColor || "",
              country: profileData.country || "",
              city: profileData.city || "",
              zipCode: profileData.zipCode || "",
              denomination: profileData.denomination || "",
              attendanceFrequency: profileData.attendanceFrequency || "",
              spiritualMaturity: profileData.spiritualMaturity || "",
              prayerFrequency: profileData.prayerFrequency || "",
              bibleReadingFrequency: profileData.bibleReadingFrequency || "",
              churchInvolvement: profileData.churchInvolvement || "",
              conversionAge: profileData.conversionAge || "",
              favoriteBibleVerse: profileData.favoriteBibleVerse || "",
              importantDoctrine: profileData.importantDoctrine || "",
              missionTripExperience: profileData.missionTripExperience || "",
              aboutMe: profileData.aboutMe || "",
              personalTestimony: profileData.personalTestimony || "",
              faithJourney: profileData.faithJourney || "",
              ministryInterests: profileData.ministryInterests || [],
              serviceActivities: profileData.serviceActivities || [],
              educationLevel: profileData.educationLevel || "",
              occupation: profileData.occupation || "",
              annualIncome: profileData.annualIncome || "",
              lookingFor: profileData.lookingFor || "",
              dealBreakers: profileData.dealBreakers || "",
              relationshipGoals: profileData.relationshipGoals || "",
              familyPlans: profileData.familyPlans || "",
              marriageViews: profileData.marriageViews || "",
              hobbies: profileData.hobbies || [],
              languagesSpoken: profileData.languagesSpoken || []
            });
          }
        }
      }
      
      console.log("Total results found:", results.length);
      setSearchResults(results);
      setSearchPerformed(true);
    } catch (error) {
      console.error("Error searching for users:", error);
      setSearchPerformed(true);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearch = async (e) => {
    if (e.type === 'click' || (e.type === 'keydown' && e.key === 'Enter')) {
      e.preventDefault();
      await performSearch();
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      window.location.href = '/signup';
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-stone-50">
        <Loader className="animate-spin h-10 w-10 text-amber-600" />
        <p className="ml-2 text-stone-700">Loading...</p>
      </div>
    );
  }

  return (
    <>
    <Header/>
    <div className="max-w-6xl mx-auto p-4 bg-stone-50 min-h-screen">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-center text-stone-800">Find Other Christians</h1>
        <p className="text-center text-stone-600 mt-2">Search for users by exact username to connect with fellow believers</p>
      </div>

      <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-stone-200">
      
        <div className="p-4 md:p-6">
          {/* Search Section */}
          <div className="mb-8">
            <form onSubmit={(e) => e.preventDefault()} className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-grow">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearch}
                  placeholder="Enter exact username to search..."
                  className="w-full p-4 border border-stone-300 rounded-lg pl-12 pr-12 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
                />
                <Search className="absolute left-4 top-4 text-stone-400" size={20} />
                
                {searchTerm && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="absolute right-4 top-4 text-stone-400 hover:text-stone-600 transition-colors"
                  >
                    <X size={20} />
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={handleSearch}
                disabled={isSearching || !searchTerm.trim()}
                className="bg-amber-600 text-white px-6 py-3 rounded-lg hover:bg-amber-700 transition duration-200 flex items-center justify-center font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSearching ? (
                  <Loader className="animate-spin h-5 w-5" />
                ) : (
                  <span>Search Users</span>
                )}
              </button>
            </form>
          </div>

          {/* Results Section */}
          {isSearching && (
            <div className="text-center py-12">
              <Loader className="animate-spin h-10 w-10 text-amber-600 mx-auto mb-4" />
              <p className="text-stone-600">Searching for users...</p>
            </div>
          )}

          {!isSearching && searchPerformed && searchResults.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold mb-6 text-stone-800">
                Search Results ({searchResults.length} user{searchResults.length !== 1 ? 's' : ''} found)
              </h2>
              <div className="space-y-8">
                {searchResults.map(user => (
                  <div key={user.id} className="border border-stone-200 rounded-lg overflow-hidden shadow-lg">
                    {/* Profile Header */}
                    <div className="bg-gradient-to-br from-amber-50 to-stone-100 p-6 border-b border-stone-200">
                      <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                        <div className="flex-shrink-0 flex justify-center lg:justify-start">
                          {user.photoUrl ? (
                            <img
                              src={user.photoUrl}
                              alt={`${user.firstName} ${user.lastName}`}
                              className="w-32 h-32 object-cover rounded-full border-4 border-white shadow-lg"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            className={`w-32 h-32 bg-stone-200 rounded-full border-4 border-white shadow-lg flex items-center justify-center ${user.photoUrl ? 'hidden' : ''}`}
                          >
                            <User size={48} className="text-stone-400" />
                          </div>
                        </div>
                        
                        <div className="flex-1 space-y-4">
                          <div className="text-center lg:text-left">
                            <h2 className="text-2xl font-bold text-stone-800 mb-1">
                              {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : 'Name not provided'}
                            </h2>
                            <p className="text-stone-500 mb-2">@{user.username}</p>
                            {user.age && (
                              <div className="flex items-center justify-center lg:justify-start text-amber-600 mb-3">
                                <Calendar className="h-4 w-4 mr-2" />
                                <span className="font-medium">{user.age} years old</span>
                              </div>
                            )}
                          </div>

                          {/* Quick Info Grid */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {(user.city || user.country) && (
                              <div className="bg-white rounded-lg p-3 shadow-sm border border-stone-200">
                                <div className="flex items-center text-stone-600">
                                  <MapPin className="h-4 w-4 mr-2 text-amber-600 flex-shrink-0" />
                                  <span className="truncate">{[user.city, user.country].filter(Boolean).join(', ')}</span>
                                </div>
                              </div>
                            )}

                            {user.denomination && (
                              <div className="bg-white rounded-lg p-3 shadow-sm border border-stone-200">
                                <div className="flex items-center text-stone-600">
                                  <Church className="h-4 w-4 mr-2 text-amber-600 flex-shrink-0" />
                                  <span className="truncate">{user.denomination}</span>
                                </div>
                              </div>
                            )}

                            {user.occupation && (
                              <div className="bg-white rounded-lg p-3 shadow-sm border border-stone-200">
                                <div className="flex items-center text-stone-600">
                                  <Briefcase className="h-4 w-4 mr-2 text-amber-600 flex-shrink-0" />
                                  <span className="truncate">{user.occupation}</span>
                                </div>
                              </div>
                            )}

                            {user.educationLevel && (
                              <div className="bg-white rounded-lg p-3 shadow-sm border border-stone-200">
                                <div className="flex items-center text-stone-600">
                                  <GraduationCap className="h-4 w-4 mr-2 text-amber-600 flex-shrink-0" />
                                  <span className="truncate">{user.educationLevel}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-6">
                    
                   {/* Personal Details */}
                     {(user.height || user.hairColor || user.eyeColor || user.ethnicity) && (
                        <div className="mb-6">
                          <h3 className="text-lg font-semibold text-stone-800 mb-3">Personal Details</h3>
                          <div className="bg-stone-50 p-4 rounded-lg border border-stone-200">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              {user.height && (
                                <div className="text-center">
                                  <span className="text-sm text-stone-500 block">Height</span>
                                  <span className="font-medium text-stone-800">{user.height}</span>
                                </div>
                              )}
                              {user.hairColor && (
                                <div className="text-center">
                                  <span className="text-sm text-stone-500 block">Hair</span>
                                  <span className="font-medium text-stone-800">{user.hairColor}</span>
                                </div>
                              )}
                              {user.eyeColor && (
                                <div className="text-center">
                                  <span className="text-sm text-stone-500 block">Eyes</span>
                                  <span className="font-medium text-stone-800">{user.eyeColor}</span>
                                </div>
                              )}
                              {user.ethnicity && (
                                <div className="text-center">
                                  <span className="text-sm text-stone-500 block">Ethnicity</span>
                                  <span className="font-medium text-stone-800">{user.ethnicity}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
</div>
                    {/* Main Content */}
                    <div className="p-6">
                      {/* About Me & Faith Journey */}
                      {(user.aboutMe || user.faithJourney) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                          {user.aboutMe && (
                            <div>
                              <h3 className="text-lg font-semibold text-stone-800 mb-3">About Me</h3>
                              <div className="bg-stone-50 p-4 rounded-lg border border-stone-200">
                                <p className="text-stone-700 leading-relaxed">{user.aboutMe}</p>
                              </div>
                            </div>
                          )}

                          {user.faithJourney && (
                            <div>
                              <h3 className="text-lg font-semibold text-stone-800 mb-3">My Faith Journey</h3>
                              <div className="bg-stone-50 p-4 rounded-lg border border-stone-200">
                                <p className="text-stone-700 leading-relaxed">{user.faithJourney}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Personal Testimony */}
                      {user.personalTestimony && (
                        <div className="mb-6">
                          <h3 className="text-lg font-semibold text-stone-800 mb-3">Personal Testimony</h3>
                          <div className="bg-stone-50 p-4 rounded-lg border border-stone-200">
                            <p className="text-stone-700 leading-relaxed">{user.personalTestimony}</p>
                          </div>
                        </div>
                      )}

                      {/* Bible Verse and Important Doctrine */}
                      {(user.favoriteBibleVerse || user.importantDoctrine) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                          {user.favoriteBibleVerse && (
                            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                              <h3 className="text-lg font-semibold text-amber-800 flex items-center mb-3">
                                <Book className="h-5 w-5 mr-2" />
                                Favorite Bible Verse
                              </h3>
                              <p className="text-stone-700 italic leading-relaxed">"{user.favoriteBibleVerse}"</p>
                            </div>
                          )}

                          {user.importantDoctrine && (
                            <div className="bg-stone-100 p-4 rounded-lg border border-stone-300">
                              <h3 className="text-lg font-semibold text-stone-800 mb-3">Important Doctrine</h3>
                              <p className="text-stone-700 leading-relaxed">{user.importantDoctrine}</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Spiritual Life Details */}
                      {(user.spiritualMaturity || user.attendanceFrequency || user.prayerFrequency || 
                       user.bibleReadingFrequency || user.conversionAge || user.missionTripExperience || user.churchInvolvement) && (
                        <div className="mb-6">
                          <h3 className="text-lg font-semibold text-stone-800 mb-3">Spiritual Life</h3>
                          <div className="bg-stone-50 p-4 rounded-lg border border-stone-200">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {user.spiritualMaturity && (
                                <div>
                                  <span className="font-medium text-amber-700">Spiritual Maturity:</span>
                                  <span className="ml-2 text-stone-700">{user.spiritualMaturity}</span>
                                </div>
                              )}
                              {user.attendanceFrequency && (
                                <div>
                                  <span className="font-medium text-amber-700">Church Attendance:</span>
                                  <span className="ml-2 text-stone-700">{user.attendanceFrequency}</span>
                                </div>
                              )}
                              {user.prayerFrequency && (
                                <div>
                                  <span className="font-medium text-amber-700">Prayer Frequency:</span>
                                  <span className="ml-2 text-stone-700">{user.prayerFrequency}</span>
                                </div>
                              )}
                              {user.bibleReadingFrequency && (
                                <div>
                                  <span className="font-medium text-amber-700">Bible Reading:</span>
                                  <span className="ml-2 text-stone-700">{user.bibleReadingFrequency}</span>
                                </div>
                              )}
                              {user.conversionAge && (
                                <div>
                                  <span className="font-medium text-amber-700">Age of Conversion:</span>
                                  <span className="ml-2 text-stone-700">{user.conversionAge}</span>
                                </div>
                              )}
                              {user.missionTripExperience && (
                                <div>
                                  <span className="font-medium text-amber-700">Mission Experience:</span>
                                  <span className="ml-2 text-stone-700">{user.missionTripExperience}</span>
                                </div>
                              )}
                              {user.churchInvolvement && (
                                <div className="md:col-span-2">
                                  <span className="font-medium text-amber-700">Church Involvement:</span>
                                  <span className="ml-2 text-stone-700">{user.churchInvolvement}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Ministry & Service */}
                      {(user.ministryInterests?.length > 0 || user.serviceActivities?.length > 0) && (
                        <div className="mb-6">
                          <h3 className="text-lg font-semibold text-stone-800 mb-3">Ministry & Service</h3>
                          <div className="bg-stone-50 p-4 rounded-lg border border-stone-200 space-y-4">
                            {user.ministryInterests?.length > 0 && (
                              <div>
                                <h4 className="font-medium text-stone-800 mb-2">Ministry Interests</h4>
                                <div className="flex flex-wrap gap-2">
                                  {user.ministryInterests.map((interest, index) => (
                                    <span key={index} className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm border border-amber-200">
                                      {interest}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {user.serviceActivities?.length > 0 && (
                              <div>
                                <h4 className="font-medium text-stone-800 mb-2">Service Activities</h4>
                                <div className="flex flex-wrap gap-2">
                                  {user.serviceActivities.map((activity, index) => (
                                    <span key={index} className="bg-stone-200 text-stone-800 px-3 py-1 rounded-full text-sm">
                                      {activity}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Relationship Goals */}
                      {(user.lookingFor || user.relationshipGoals || user.familyPlans || user.marriageViews || user.dealBreakers) && (
                        <div className="mb-6">
                          <h3 className="text-lg font-semibold text-stone-800 mb-3">Relationship & Marriage</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {(user.lookingFor || user.relationshipGoals || user.familyPlans || user.marriageViews) && (
                              <div className="bg-stone-50 p-4 rounded-lg border border-stone-200 space-y-3">
                                {user.lookingFor && (
                                  <div>
                                    <span className="font-medium text-amber-700">Looking For:</span>
                                    <p className="text-stone-700 mt-1">{user.lookingFor}</p>
                                  </div>
                                )}
                                {user.relationshipGoals && (
                                  <div>
                                    <span className="font-medium text-amber-700">Relationship Goals:</span>
                                    <p className="text-stone-700 mt-1">{user.relationshipGoals}</p>
                                  </div>
                                )}
                                {user.familyPlans && (
                                  <div>
                                    <span className="font-medium text-amber-700">Family Plans:</span>
                                    <p className="text-stone-700 mt-1">{user.familyPlans}</p>
                                  </div>
                                )}
                                {user.marriageViews && (
                                  <div>
                                    <span className="font-medium text-amber-700">Views on Marriage:</span>
                                    <p className="text-stone-700 mt-1">{user.marriageViews}</p>
                                  </div>
                                )}
                              </div>
                            )}

                            {user.dealBreakers && (
                              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                                <h4 className="font-medium text-red-800 mb-2">Deal Breakers</h4>
                                <p className="text-stone-700 leading-relaxed">{user.dealBreakers}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Hobbies & Languages */}
                      {(user.hobbies?.length > 0 || user.languagesSpoken?.length > 0) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                          {user.hobbies?.length > 0 && (
                            <div>
                              <h3 className="text-lg font-semibold text-stone-800 mb-3">Hobbies & Interests</h3>
                              <div className="bg-stone-50 p-4 rounded-lg border border-stone-200">
                                <div className="flex flex-wrap gap-2">
                                  {user.hobbies.map((hobby, index) => (
                                    <span key={index} className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm border border-amber-200">
                                      {hobby}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}

                          {user.languagesSpoken?.length > 0 && (
                            <div>
                              <h3 className="text-lg font-semibold text-stone-800 mb-3">Languages Spoken</h3>
                              <div className="bg-stone-50 p-4 rounded-lg border border-stone-200">
                                <div className="flex flex-wrap gap-2">
                                  {user.languagesSpoken.map((language, index) => (
                                    <span key={index} className="bg-stone-300 text-stone-800 px-3 py-1 rounded-full text-sm">
                                      {language}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Message Button */}
                      <div className="flex justify-center pt-4 border-t border-stone-200">
                        <Link
                          to={`/chat/${user.id}`}
                          className="bg-amber-600 text-white px-8 py-3 rounded-lg hover:bg-amber-700 transition duration-200 flex items-center font-medium shadow-lg"
                        >
                          <MessageCircle className="mr-2" size={18} />
                          Send Message
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!isSearching && searchPerformed && searchResults.length === 0 && (
            <div className="text-center py-12 text-stone-500">
              <User size={64} className="mx-auto mb-4 text-stone-400" />
              <h3 className="text-xl font-medium mb-2">No users found</h3>
              <p>No users found matching "{searchTerm}". Please check the username and try again.</p>
            </div>
          )}

          {!searchPerformed && !isSearching && (
            <div className="text-center py-12 text-stone-500">
              <Search size={64} className="mx-auto mb-4 text-stone-400" />
              <h3 className="text-xl font-medium mb-2">Search for Christians</h3>
              <p>Enter a username above to find and connect with other believers.</p>
            </div>
          )}
        </div>
      </div>
    </div>
    <Footer/>
    </>
  );
}